/**
 * Notification Settings Component
 *
 * Provides UI for managing web push notification subscriptions and preferences.
 * Subscriptions are browser-specific and identified by a unique ID stored in localStorage.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  subscribeToNotifications,
  unsubscribeFromNotifications,
  updatePreferences,
  getPreferences,
  isSubscriptionActive,
  isPushSupported,
  getNotificationPermission,
} from '@/services/webpush';
import type { NotificationPreferences } from '@/types/notifications';
import { DEFAULT_NOTIFICATION_PREFERENCES } from '@/types/notifications';

interface NotificationSettingsProps {
  onSubscriptionChange?: (isSubscribed: boolean) => void;
}

export function NotificationSettings({ onSubscriptionChange }: NotificationSettingsProps): JSX.Element {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [preferences, setPreferences] = useState<NotificationPreferences>(DEFAULT_NOTIFICATION_PREFERENCES);
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const checkSubscriptionStatus = useCallback(async (): Promise<void> => {
    const active = await isSubscriptionActive();
    setIsSubscribed(active);
    onSubscriptionChange?.(active);
  }, [onSubscriptionChange]);

  useEffect(() => {
    checkSupport();
    void checkSubscriptionStatus();
    void loadPreferences();
  }, [checkSubscriptionStatus]);

  function checkSupport(): void {
    const supported = isPushSupported();
    setIsSupported(supported);
    if (supported) {
      setPermission(getNotificationPermission());
    }
  }

  async function loadPreferences(): Promise<void> {
    const prefs = await getPreferences();
    if (prefs !== null) {
      setPreferences(prefs.preferences);
    }
  }

  async function handleSubscribe(): Promise<void> {
    setLoading(true);
    setStatusMessage(null);

    const subscriptionId = await subscribeToNotifications(preferences);

    if (subscriptionId !== null) {
      setIsSubscribed(true);
      onSubscriptionChange?.(true);
      setStatusMessage({ type: 'success', text: 'Successfully subscribed to notifications!' });
      setPermission(getNotificationPermission());
    } else {
      setStatusMessage({ type: 'error', text: 'Failed to subscribe. Check browser permissions.' });
    }

    setLoading(false);
  }

  async function handleUnsubscribe(): Promise<void> {
    setLoading(true);
    setStatusMessage(null);

    const success = await unsubscribeFromNotifications();

    if (success) {
      setIsSubscribed(false);
      onSubscriptionChange?.(false);
      setStatusMessage({ type: 'success', text: 'Successfully unsubscribed from notifications.' });
    } else {
      setStatusMessage({ type: 'error', text: 'Failed to unsubscribe.' });
    }

    setLoading(false);
  }

  async function handlePreferencesChange(newPrefs: NotificationPreferences): Promise<void> {
    setPreferences(newPrefs);

    // Update backend
    const success = await updatePreferences(newPrefs);
    if (!success) {
      setStatusMessage({ type: 'error', text: 'Failed to update preferences.' });
    }
  }

  function handleDollarThresholdChange(value: string): void {
    const cents = value === '' ? null : Number(value) * 100;
    void handlePreferencesChange({
      ...preferences,
      auctions: {
        ...preferences.auctions,
        min_usd_threshold_cents: cents,
      },
    });
  }

  if (!isSupported) {
    return (
      <div className="p-6 bg-gray-100 rounded-lg border border-gray-300">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Push Notifications</h3>
        <p className="text-gray-600">
          Push notifications are not supported by your browser. Please use a modern browser like Chrome, Firefox, or
          Edge.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header and Status */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Browser Push Notifications</h3>
            <p className="text-sm text-gray-600 mt-1">
              Get notified about new auctions and governance proposals, even when the app is closed.
            </p>
          </div>
          {isSubscribed ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              ✓ Enabled
            </span>
          ) : (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
              Disabled
            </span>
          )}
        </div>

        {/* Status Message */}
        {statusMessage && (
          <div
            className={`mb-4 p-3 rounded ${
              statusMessage.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}
          >
            {statusMessage.text}
          </div>
        )}

        {/* Permission Status */}
        {permission === 'denied' && (
          <div className="mb-4 p-3 rounded bg-yellow-50 text-yellow-800">
            Notification permission is blocked. Please enable it in your browser settings.
          </div>
        )}

        {/* Subscribe/Unsubscribe */}
        {!isSubscribed ? (
          <div className="space-y-3">
            <button
              onClick={() => void handleSubscribe()}
              disabled={loading || permission === 'denied'}
              className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {loading ? 'Subscribing...' : 'Enable Notifications'}
            </button>
          </div>
        ) : (
          <button
            onClick={() => void handleUnsubscribe()}
            disabled={loading}
            className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            {loading ? 'Unsubscribing...' : 'Disable Notifications'}
          </button>
        )}
      </div>

      {/* Preferences (only shown when subscribed) */}
      {isSubscribed && (
        <>
          {/* Master Toggle */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <label className="flex items-center justify-between cursor-pointer">
              <div>
                <h4 className="text-md font-semibold text-gray-900">Enable All Notifications</h4>
                <p className="text-sm text-gray-600 mt-1">Master switch for all notification types</p>
              </div>
              <input
                type="checkbox"
                checked={preferences.enabled}
                onChange={(e) => void handlePreferencesChange({ ...preferences, enabled: e.target.checked })}
                className="h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
            </label>
          </div>

          {/* Auction Notifications */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h4 className="text-md font-semibold text-gray-900 mb-4">Auction Notifications</h4>

            <div className="space-y-4">
              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.auctions.notify_new_auctions}
                  onChange={(e) =>
                    void handlePreferencesChange({
                      ...preferences,
                      auctions: { ...preferences.auctions, notify_new_auctions: e.target.checked },
                    })
                  }
                  disabled={!preferences.enabled}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-gray-900">Notify of new auctions</span>
                  <p className="text-sm text-gray-600">Get alerted when new auctions become available</p>
                </div>
              </label>

              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.auctions.notify_new_bids}
                  onChange={(e) =>
                    void handlePreferencesChange({
                      ...preferences,
                      auctions: { ...preferences.auctions, notify_new_bids: e.target.checked },
                    })
                  }
                  disabled={!preferences.enabled}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-gray-900">Notify of new bids</span>
                  <p className="text-sm text-gray-600">Get alerted when someone places a new bid on any auction</p>
                </div>
              </label>

              {preferences.auctions.notify_new_auctions && (
                <div className="ml-7 mt-3 bg-gray-50 p-4 rounded-lg">
                  <label className="block">
                    <span className="text-sm font-medium text-gray-700">Minimum auction value (USD)</span>
                    <div className="mt-2 flex items-center">
                      <span className="text-gray-500 mr-2">$</span>
                      <input
                        type="number"
                        min="0"
                        step="1"
                        placeholder="Any amount"
                        value={
                          preferences.auctions.min_usd_threshold_cents === null
                            ? ''
                            : preferences.auctions.min_usd_threshold_cents / 100
                        }
                        onChange={(e) => handleDollarThresholdChange(e.target.value)}
                        disabled={!preferences.enabled}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:opacity-50"
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">Leave empty to be notified of all auctions</p>
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Governance Notifications */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h4 className="text-md font-semibold text-gray-900 mb-4">Governance Notifications</h4>

            <div className="space-y-4">
              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.governance.notify_new_proposals}
                  onChange={(e) =>
                    void handlePreferencesChange({
                      ...preferences,
                      governance: { ...preferences.governance, notify_new_proposals: e.target.checked },
                    })
                  }
                  disabled={!preferences.enabled}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-gray-900">New proposals submitted</span>
                  <p className="text-sm text-gray-600">Get notified when new governance proposals are created</p>
                </div>
              </label>

              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.governance.notify_voting_started}
                  onChange={(e) =>
                    void handlePreferencesChange({
                      ...preferences,
                      governance: { ...preferences.governance, notify_voting_started: e.target.checked },
                    })
                  }
                  disabled={!preferences.enabled}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-gray-900">Voting period started</span>
                  <p className="text-sm text-gray-600">Get notified when proposals enter the voting period</p>
                </div>
              </label>

              <label className="flex items-start cursor-pointer">
                <input
                  type="checkbox"
                  checked={preferences.governance.notify_voting_complete}
                  onChange={(e) =>
                    void handlePreferencesChange({
                      ...preferences,
                      governance: { ...preferences.governance, notify_voting_complete: e.target.checked },
                    })
                  }
                  disabled={!preferences.enabled}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 disabled:opacity-50"
                />
                <div className="ml-3">
                  <span className="text-sm font-medium text-gray-900">Voting complete (with result)</span>
                  <p className="text-sm text-gray-600">Get notified when voting ends with the result (Passed/Rejected/Failed)</p>
                </div>
              </label>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  These notifications are specific to this browser. If you use multiple devices or browsers, you'll
                  need to enable notifications separately on each one.
                </p>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
